"use client";

import { useRef } from "react";

/**
 * Returns a debounced version of the provided callback function.
 * The callback will be executed after the specified delay has passed
 * since the last invocation, HOWEVER, it will also be executed if the
 * specified maximum time has passed since the first invocation.
 *
 * @param callback - The function to debounce
 * @param delayMs - Delay in milliseconds (default: 200)
 * @param requireMs - Time at which the function is called anyway (default: 600)
 * @returns A debounced version of the callback
 *
 * @example
 * const debouncedSearch = useMinMaxCallback(
 *   async (query: string) => await searchApi(query),
 *   300,
 *   1000
 * );
 * // Later: debouncedSearch("hello");
 */
export const useMinMaxCallback = <TProps extends any[] = any[], TReturn = void>(
  callback: (...props: TProps) => Promise<TReturn>,
  delayMs: number = 200,
  requireMs: number = 600,
): ((...props: TProps) => Promise<TReturn>) => {
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const firstUnmetRequest = useRef<number | null>(null);

  return (...props: TProps) => {
    if (!firstUnmetRequest.current) firstUnmetRequest.current = Date.now();
    if (timeout.current) clearTimeout(timeout.current);
    return new Promise<TReturn>((resolve) => {
      if (Date.now() - firstUnmetRequest.current > requireMs) {
        resolve(callback(...props));
        firstUnmetRequest.current = null;
        return;
      }
      timeout.current = setTimeout(() => {
        resolve(callback(...props));
        firstUnmetRequest.current = null;
      }, delayMs);
    });
  };
};
